export const sortByArray = (arr: string[], order: readonly string[]): string[] =>
  arr.sort((a, b) => {
    const indexA = order.indexOf(a.toLowerCase());
    const indexB = order.indexOf(b.toLowerCase());
    return (indexA === -1 ? Number.POSITIVE_INFINITY : indexA) - (indexB === -1 ? Number.POSITIVE_INFINITY : indexB);
  });

/**
 * Predicate that filters out duplicates in an array
 * @param value Current value
 * @param index Current index
 * @param self The array
 * @returns `true` when the element is not a duplicate, `false` otherwise
 * @example
 * ```
 * [1, 2, 3, 2].filter(distinct) // returns [1, 2, 3]
 * ```
 */
export function distinct<T>(value: T, index: number, self: readonly T[]) {
  return self.indexOf(value) === index;
}

export function addOrRemoveIfExists<T>(values: readonly T[], value: T): readonly T[] {
  const idx = values.indexOf(value);
  if (idx < 0) {
    return [...values, value];
  }
  const newValues = [...values];
  return newValues.splice(idx, 1);
}

/**
 * Function to get the intersection between two arrays
 * @param arrayA First array
 * @param arrayB Second array
 * @returns array of common elements between arrayA and arrayB
 * @example
 * ```
 * intersect([1,2,3], [2,3,4]) // returns [2, 3]
 * ```
 */
export function intersect<T>(arrayA: readonly T[], arrayB: readonly T[]) {
  return arrayA.filter(x => arrayB.includes(x));
}

/**
 * Function to determine if two arrays are identical
 * @param arrayA First array
 * @param arrayB Second array
 * @returns boolean
 * @example
 * ```
 * areArraysEqual([1,2,3], [3,2,1]) // returns true
 * ```
 */
export function areArraysEqual<T>(arrayA: readonly T[], arrayB: readonly T[]) {
  return arrayA.length === arrayB.length && arrayA.every((element, index) => element === arrayB[index]);
}
